import { DOCUMENT, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { DataTableColumn, DataTableComplexLabel, DataTableState } from './DataTable';
import { NumberOrString as NumberOrStringPipe } from './pipes/number-or-string.pipe';

@Injectable()
export class DataTable {
  private readonly _$state: WritableSignal<DataTableState> = signal({ isCollapsed: false });
  readonly $state: Signal<DataTableState> = this._$state.asReadonly();
  private readonly _mediaQueryBrowserHasPointer = '(hover:hover) and (any-pointer:fine)';
  private readonly _$isBrowserHasPointer: WritableSignal<boolean>;
  readonly $browserHasPointer!: Signal<boolean>;
  private readonly _numberOrStringPipe: NumberOrStringPipe = new NumberOrStringPipe();
  private readonly _document: Document = inject(DOCUMENT);

  constructor() {
    this._$isBrowserHasPointer = signal<boolean>(
      this._document.defaultView!.matchMedia(this._mediaQueryBrowserHasPointer).matches
    );
    this.$browserHasPointer = this._$isBrowserHasPointer.asReadonly();
    this._document
      .defaultView!.matchMedia(this._mediaQueryBrowserHasPointer)
      .addEventListener('change', this._onMediaQueryBrowserHasPointerChanged.bind(this));
  }

  /**
   * @description
   * @param initialState WritableSignal<OkeDataTableState>
   * @returns void
   */
  initState(initialState: WritableSignal<DataTableState>): void {
    this._$state.set(initialState());
  }

  getSanitizedVisibleColumns<T>(
    allColumns: DataTableColumn<T>[],
    hiddenColumnKeys: Set<keyof T>,
    data: T[]
  ): DataTableColumn<T>[] {
    const isFilteredColumn = (col: DataTableColumn<T>): boolean => !hiddenColumnKeys.has(col.key);
    const getRemappedColumn = (col: DataTableColumn<T>): DataTableColumn<T> => {
      const { key } = col;
      const keyedData = data.map(value => value[key]);
      const isNumerical: boolean = keyedData.reduce(
        (hasNumber, value) => hasNumber === true || this._numberOrStringPipe.transform(value) === 'number',
        false
      );
      return {
        ...col,
        isNumerical
      };
    };
    return allColumns.filter(isFilteredColumn).map(getRemappedColumn);
  }

  isLabelComplex(label: any): label is DataTableComplexLabel {
    return label !== null && typeof label === 'object';
  }

  normalizeForSort(value: string | number | null | undefined): string | number {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'number') {
      return value;
    }
    let stringValue = String(value).trim();
    let isAccountingNegative = false;
    // Narrowing the type to safely check for parentheses
    if (typeof stringValue === 'string') {
      if (stringValue.startsWith('(') && stringValue.endsWith(')')) {
        isAccountingNegative = true;
        // Strip the opening and closing parentheses
        stringValue = stringValue.slice(1, -1);
      }
    }
    // Strip out currency symbols, commas, and percentage signs
    const cleanedValue = stringValue.replace(/[,$\xA3\u20AC%]/g, '');
    const numericValue = Number(cleanedValue);
    // If it's a valid number, return it (applying the negative sign if parentheses were present)
    if (!isNaN(numericValue) && cleanedValue !== '') {
      return isAccountingNegative ? -numericValue : numericValue;
    }
    return stringValue.toLowerCase();
  }

  private _onMediaQueryBrowserHasPointerChanged(mediaQueryListEvent: MediaQueryListEvent): void {
    const { matches: isBrowserHasPointer } = mediaQueryListEvent;
    this._$isBrowserHasPointer.update(() => isBrowserHasPointer);
  }

  set isCollapsed(isCollapsed: boolean) {
    this._$state.update($state => ({
      ...$state,
      isCollapsed
    }));
  }
}
